import type { AudiHeaderConfig } from '../interfaces/header-components.interfaces';
import { matchCountryUrlOrigin } from './match-url-pattern';

/**
 * Appends the partner_id query parameter to a given URL.
 * @param {string} urlToAppend - The URL to append the partner_id query parameter to.
 * @param {string} partnerIdQueryString - The partner_id query string to append.
 * @param {AudiHeaderConfig|undefined} [config] - Optional configuration object to check if the URL matches the tier one URL.
 * @returns {string} The URL with the partner_id query parameter appended, or the original URL if it does not match the tier one URL.
 */
export const appendPartnerID = (
  urlToAppend: string,
  partnerIdQueryString: string,
  config?: AudiHeaderConfig,
) => {
  const pattern = /(http|https|www)/i;
  const isAbsoluteUrl = pattern.test(urlToAppend);
  const url = !isAbsoluteUrl ? `${window?.location.protocol}//${urlToAppend}` : urlToAppend;

  if (matchCountryUrlOrigin(url, config?.tierOneUrl || '')) {
    const urlObj = new URL(url);
    urlObj.searchParams.append('partner_id', partnerIdQueryString);
    return urlObj.toString();
  }

  return urlToAppend;
};

/**
 * Removes the partner_id query parameter from the current URL.
 * @param {string} [urlToRemoveFrom=window.location.href] - The URL to remove the partner_id query parameter from.
 * @returns {void}
 */
export const removePartnerID = () => {
  const pattern = /(http|https|www)/i;
  const urlToRemoveFrom = (typeof window !== 'undefined' && window.location.href) || '';
  const isAbsoluteUrl = pattern.test(urlToRemoveFrom);
  const url = !isAbsoluteUrl ? `${window?.location.protocol}//${urlToRemoveFrom}` : urlToRemoveFrom;

  const urlObj = new URL(url);
  urlObj.searchParams.delete('partner_id');
  // update url
  if (typeof window !== 'undefined')
    window.history.replaceState({}, document.title, urlObj.toString());
};

/**
 * Checks if a query parameter exists in the current URL.
 * @param {string | null | undefined} param - The query parameter to search for.
 * @returns {boolean} True if the URL's query string contains the parameter, false otherwise.
 */
export function queryStringExists(param: string | null | undefined) {
  if (param === null || param === undefined) {
    return false;
  }
  const urlParams =
    typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search) : null;
  return param ? urlParams?.has(param) : false;
}

/**
 * Checks if the URL's hash contains a specific parameter.
 * @param {string} param - The parameter to search for.
 * @returns {boolean} True if the URL's hash contains the parameter, false otherwise.
 */
export function getDealerSearchHashParam(param: string) {
  const hash = typeof window !== 'undefined' ? window.location.hash.substring(1) : ''; // Remove the #
  const hasParam = hash.includes(param);
  return hasParam;
}

/**
 * Retrieves the value of a query parameter from a given URL.
 * @param {string} url - The URL to retrieve the value from.
 * @param {string} param - The query parameter to retrieve the value of.
 * @returns {string | null} The value of the query parameter, or null if the parameter does not exist in the URL.
 */
export function getValueFromQueryString(url: string, param: string) {
  const splitUrl = url.split('?');
  if (splitUrl?.length > 0) {
    return splitUrl[splitUrl.length - 1].includes(param);
  }
  return null;
}
